
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import {
  deleteFile,
  fetchFoldersInfo,
  assignFiles,
} from "@store/thunks/dashboardThunk";
import { setSelectedTargetId, setSelectedTargetName, setSeletedFileId } from "@store/slices/dashboardSlice";
import { openModal } from "@store/slices/modalSlice";
import CreateProject from "@components/modals/CreateProject";
import CreateFolder from "@components/modals/CreateFolder";
import FileUpload from "@components/modals/FileUpload";
import { formatFileSize, languageIdToName, restrictSpecialCharacters } from "@utils/constants";
import { toast } from "react-toastify";

const DevnagriFileTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { files, selectedFolderId, createdFor } = useSelector((store) => store.dashboard);
  const { isOpen: isFileUploadOpen } = useSelector((store) => store.modal.fileUpload || { isOpen: false });
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const fileArray = files[selectedFolderId] || [];
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const [activeAssignFileId, setActiveAssignFileId] = useState(null); // Track file with active popup

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true);
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const fetchFiles = async () => {
    if (selectedFolderId) {
      setIsLoading(true);
      try {
        const res = await dispatch(fetchFoldersInfo("devnagri", selectedFolderId, searchQuery, pageIndex, pageSize));
        if (Array.isArray(res?.data)) {
          if (res.data.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedFolderId) {
      fetchFiles();
    } else {
      navigate("/superAdmin/devnagri/folders");
    }
  }, [dispatch, searchQuery, selectedFolderId, pageIndex, pageSize, navigate, lastRefresh]);

  useEffect(() => {
    if (!isFileUploadOpen) {
      setLastRefresh(Date.now());
    }
  }, [isFileUploadOpen]);

  const handleFileClick = ({ row }) => {
    console.log("Row data:", row);
    const fileId = row.original?.fileId;
    const targetLanguageId = row.original?.targetId;
    const targetLanguageName = row.original?.targetName;


    if (!fileId || !targetLanguageId) {
      console.error("Required data not found in row:", { fileId, targetLanguageId, row });
      alert("Error: Could not determine file ID or target language");
      return;
    }

    dispatch(setSeletedFileId(fileId));
    dispatch(setSelectedTargetId(targetLanguageId));
    dispatch(setSelectedTargetName(targetLanguageName));
    navigate("/superAdmin/devnagri/folders/files/sentences");
  };

  const handleAssignFile = async ({ row }) => {
    console.log("[DAFileTable] handleAssignFile called with row:", row);
    const status = row.original.devStatus;
    const fileId = row.fileId || (row.original && row.original.fileId);
    const fileName = row.fileName || (row.original && row.original.fileName) || "Unknown File";

    if (!fileId) {
      console.log("[DAFileTable] Missing fileId in row:", row);
      toast.error(
        <div className="p-3 font-serif text-sm font-medium text-gray-800">
          Error: Could not determine file ID
        </div>,
        {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
          className: "rounded-lg shadow-md border border-red-200",
        }
      );
      return;
    }

    const currentFiles = files[selectedFolderId] || [];
    const fileData = currentFiles.find(file => file.fileId === fileId);
    console.log("[DAFileTable] Complete file data for assignment:", fileData);

    setActiveAssignFileId(fileId); // Disable button for this file
    const toastId = toast(
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200 max-w-md font-serif">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Confirm Assignment</h3>
          <button
            onClick={() => {
              console.log("[DAFileTable] Assign cancelled via close button for fileId:", fileId);
              toast.dismiss(toastId);
              setActiveAssignFileId(null); // Re-enable button
            }}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium focus:outline-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="text-gray-700 text-sm mb-4">
          Assign file <strong>{fileName}</strong> to Devnagri Admin?
        </p>
        <div className="flex gap-3">
          <button
            onClick={async () => {
              console.log("[DAFileTable] Assign confirmed for fileId:", fileId);
              toast.dismiss(toastId);
              setActiveAssignFileId(null); // Re-enable button
              setIsLoading(true);
              try {
                console.log("[DAFileTable] Assigning Devnagri file with ID:", fileId);
                const result = await dispatch(assignFiles("devnagri", fileId));
                console.log("[DAFileTable] Assignment result:", result);

                if (result && result.success) {
                  await fetchFiles();
                  toast.success(
                    <div className="p-3 font-serif text-sm font-medium text-gray-800">
                      File "{fileName}" has been assigned successfully!
                    </div>,
                    {
                      position: "top-right",
                      autoClose: 3000,
                      theme: "colored",
                      className: "rounded-lg shadow-md border border-green-200",
                    }
                  );
                } else {
                  toast.error(
                    <div className="p-3 font-serif text-sm font-medium text-gray-800">
                      Failed to assign file. Please try again.
                    </div>,
                    {
                      position: "top-right",
                      autoClose: 3000,
                      theme: "colored",
                      className: "rounded-lg shadow-md border border-red-200",
                    }
                  );
                }
              } catch (error) {
                console.log("[DAFileTable] Error assigning file:", error);
                toast.error(
                  <div className="p-3 font-serif text-sm font-medium text-gray-800">
                    Error: {error.message || "Failed to assign file"}
                  </div>,
                  {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                    className: "rounded-lg shadow-md border border-red-200",
                  }
                );
              } finally {
                setIsLoading(false);
              }
            }}
            className="flex-1 px-4 py-2 bg-[#ec7026] text-white rounded-lg hover:bg-[#d85d1a] focus:ring-2 focus:ring-[#ec7026]/50 text-sm font-semibold transition-colors"
          >
            Confirm
          </button>
          <button
            onClick={() => {
              console.log("[DAFileTable] Assign cancelled for fileId:", fileId);
              toast.dismiss(toastId);
              setActiveAssignFileId(null); // Re-enable button
            }}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        className: "mt-16",
      }
    );
  };

  const handleOpenFileUpload = () => {
    dispatch(openModal("fileUpload"));
  };

  const handleClickBack = () => {
    navigate(-1);
  };

  const handleClickHome = () => {
    navigate("/superAdmin");
  };

  const getStatusStyle = (status) => {
    const styles = {
      PENDING: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" },
      UPLOADED: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Uploaded" },
      GS_ASSIGNED: { bg: "bg-blue-100", text: "text-blue-800", label: "GS Assigned" },
      DEV_ASSIGNED: { bg: "bg-purple-100", text: "text-purple-800", label: "Dev Assigned" },
      TA_ASSIGNED: { bg: "bg-indigo-100", text: "text-indigo-800", label: "TA Assigned" },
      VA_ASSIGNED: { bg: "bg-pink-100", text: "text-pink-800", label: "VA Assigned" },
      DU_ASSIGNED: { bg: "bg-cyan-100", text: "text-cyan-800", label: "DU Assigned" },
      UNDER_TRANSLATION: { bg: "bg-orange-100", text: "text-orange-800", label: "Under Translation" },
      UNDER_VALIDATION: { bg: "bg-lime-100", text: "text-lime-800", label: "Under Validation" },
      DEV_VALIDATED: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Dev Validated" },
      TRANSLATED: { bg: "bg-teal-100", text: "text-teal-800", label: "Translated" },
      VALIDATED: { bg: "bg-sky-100", text: "text-sky-800", label: "Validated" },
      GS_REJECTED: { bg: "bg-rose-100", text: "text-rose-800", label: "GS Rejected" },
      DEV_REJECTED: { bg: "bg-red-100", text: "text-red-800", label: "Dev Rejected" },
      COMPLETED: { bg: "bg-green-100", text: "text-green-800", label: "Completed" },
    };
    return styles[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
  };

  const fileColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "fileName",
      header: () => "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <button
          onClick={() => handleFileClick({ row })}
          className="text-[#ec7026] hover:text-[#d85d1a] focus:underline"
        >
          {row.original.fileName}
        </button>
      ),
    },
    {
      id: "fileSize",
      header: () => "File Size",
      accessorKey: "fileSizeBytes",
      cell: ({ row }) => (
        <span className="text-gray-700">
          {formatFileSize(row.original.fileSizeBytes)}
        </span>
      ),
      cellClassName: "text-center",
    },
    {
      id: "totalSentences",
      header: () => "Total Sentences",
      accessorKey: "totalSentences",
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.totalSentences}</span>
      ),
      cellClassName: "text-center",
    },
    {
      id: "sourceName",
      header: () => "Source Language",
      accessorKey: "sourceName",
      cell: ({ row }) => (
        <span className="text-gray-700">
          {row.original.sourceName}
        </span>
      ),
      cellClassName: "text-center",
    },
    {
      id: "targetName",
      header: () => "Target Language",
      accessorKey: "targetName",
      cell: ({ row }) => (
        <span className="text-gray-700">
          {row.original.targetName}
        </span>
      ),
      cellClassName: "text-center",
    },
    {
      id: "createdAt",
      header: () => "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const deadline = row.original?.createdAt || row.original?.uploadedAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
      cellClassName: "text-center",
    },
    {
      id: "status",
      header: () => "Status",
      accessorKey: "devStatus",
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.devStatus);
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "assign",
      header: () => "Assign",
      accessorKey: "assign",
      cell: ({ row }) => {
        const isAssignable = row.original.devStatus === "UPLOADED";
        const isButtonDisabled = !isAssignable || activeAssignFileId === row.original?.fileId;
        console.log("[DAFileTable] Assign button rendered for file:", row.original?.fileId, "isAssignable:", isAssignable, "isButtonDisabled:", isButtonDisabled);
        return (
          <button
            onClick={() => isAssignable && handleAssignFile({ row })}
            disabled={isButtonDisabled}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${isButtonDisabled
              ? "border border-gray-200 text-gray-400 cursor-not-allowed"
              : "border border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50"
              }`}
          >
            {isAssignable ? "Assign" : "Assigned"}
          </button>
        );
      },
      cellClassName: "text-center",
    },
  ];

  return (
    <div className="flex flex-col h-screen mt-14">
      <CreateProject />
      <CreateFolder />
      <FileUpload />
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="flex flex-col">
              <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                <span className=" text-[#ec7026] shadow-sm">
                  Devnagri
                </span> Files</h1>
              <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                Manage and process your files
              </p>
            </div>
            <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search files..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
                  value={inputValue}
                  onChange={handleInputChange}
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <button
                onClick={handleClickHome}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Home
              </button>
              <button
                onClick={handleClickBack}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
              {popup.isOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
                    <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
                      {popup.type === "error" ? "Error" : "Info"}
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
                      {popup.message}
                    </p>
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={handlePopupClose}
                        className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {createdFor === 3 && (
                <button
                  onClick={handleOpenFileUpload}
                  className="inline-flex items-center px-3 py-2 border border-[#2563eb] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#2563eb] hover:bg-[#1d4ed8] focus:outline-none focus:ring-2 focus:ring-[#2563eb] transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Upload File
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2563eb]"></div>
              </div>
            ) : (
              <GenericTable
                data={[...fileArray].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                columns={fileColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage={searchQuery ? "No files match your search" : "No Devnagri files available"}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { DevnagriFileTable };